<template>
  <div class="solution-detail-page">
    <!-- 添加导航触发区域 -->
    <ProductNavigation />

    <!-- 解决方案页面头部section -->
    <section class="solution-hero-section">
     
    </section>
    
    <div class="solution-container">


      <!-- 加载状态 -->
      <div v-if="loading" class="loading-spinner">
        <div class="spinner"></div>
        <p>{{ t('solutions.loadingDetail') }}</p>
      </div>

      <!-- 错误状态 -->
      <div v-else-if="error" class="error-message">
        <div class="error-icon">⚠</div>
        <p>{{ error }}</p>
        <button class="retry-button" @click="loadSolutionDetail">{{ t('common.retry') }}</button>
      </div>

      <!-- 解决方案内容 -->
      <div v-else-if="solution && !loading && !error" class="solution-content">
        <!-- 解决方案头部 -->
        <div class="solution-header">
          <h1 class="solution-title">{{ 
            locale === 'en' 
              ? (solution.titleEn || solution.title) 
              : solution.title 
          }}</h1>
          <div class="solution-description" v-html="formattedDescription"></div>
        </div>

        <!-- 段落式内容 -->
        <div class="solution-sections">
          <!-- 动态段落内容 -->
          <div v-if="solution.paragraphs && solution.paragraphs.length > 0" class="dynamic-paragraphs">
            <div v-for="paragraph in activeParagraphs" :key="paragraph.id" class="section">
              <div class="section-header">
                <h2 class="section-title">{{ paragraph.title }}</h2>
                <div class="section-divider"></div>
              </div>
              <div class="section-content">
                <!-- 根据内容类型渲染不同的组件 -->
                <div v-if="paragraph.content_type === 'text'" class="paragraph-text" v-html="formatTextContent(paragraph.content)"></div>
                <div v-else-if="paragraph.content_type === 'table'" class="paragraph-table">
                  <table v-if="paragraph.content && paragraph.content.headers" class="content-table">
                    <thead>
                      <tr>
                        <th v-for="header in paragraph.content.headers" :key="header">{{ header }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(row, index) in paragraph.content.rows" :key="index">
                        <td v-for="(cell, cellIndex) in row" :key="cellIndex">{{ cell }}</td>
                      </tr>
                    </tbody>
                  </table>
                  <div v-else class="table-error">{{ t('solutions.tableFormatError') }}</div>
                </div>
                <div v-else-if="paragraph.content_type === 'image'" class="paragraph-image">
                  <img v-if="paragraph.content" :src="getImageUrl(paragraph.content)" :alt="paragraph.title" class="content-image" />
                  <div v-else class="image-error">{{ t('solutions.imageUrlInvalid') }}</div>
                </div>
                <div v-else-if="paragraph.content_type === 'mixed'" class="paragraph-mixed">
                  <div v-for="(item, index) in parseMixedContent(paragraph.content)" :key="index" class="mixed-item">
                    <div v-if="item.type === 'text'" class="mixed-text" v-html="formatTextContent(item.content)"></div>
                    <div v-else-if="item.type === 'image'" class="mixed-image">
                      <img :src="getImageUrl(item.content)" :alt="item.alt || paragraph.title" class="content-image" />
                    </div>
                    <div v-else-if="item.type === 'table'" class="mixed-table">
                      <table v-if="item.content && item.content.headers" class="content-table">
                        <thead>
                          <tr>
                            <th v-for="header in item.content.headers" :key="header">{{ header }}</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="(row, rowIndex) in item.content.rows" :key="rowIndex">
                            <td v-for="(cell, cellIndex) in row" :key="cellIndex">{{ cell }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
                <div v-else class="paragraph-fallback" v-html="formatTextContent(paragraph.content)"></div>
              </div>
            </div>
          </div>
          
          <!-- 默认段落（当没有段落数据时） -->
          <div v-else class="default-sections">
            <div class="section">
              <div class="section-header">
                <!-- 取消h2标题内容 -->
                <div class="section-divider"></div>
              </div>
              <div class="section-content">
                <div class="no-data">
                  <div class="no-data-icon">📋</div>
                  <p>{{ t('solutions.noDetailContent') }}</p>
                </div>
              </div>
            </div>
          </div>


        </div>
      </div>
    </div>
  </div>

  <!-- 页脚内容 -->
  <Footer />
</template>


<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { getSolution, getSolutionParagraphs } from '@/api/solution';
import ProductNavigation from '@/components/ProductNavigation.vue';
import Footer from '@/components/Footer.vue';

const route = useRoute();
const { t, locale } = useI18n();
const solution = ref(null);
const loading = ref(true);
const error = ref(null);

// 获取图片URL的函数 - 使用环境变量
const getImageUrl = (imagePath) => {
  if (!imagePath) return '';
  
  // 检查是否已经是完整URL
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  
  // 获取API基础URL，去掉/api后缀（因为图片路径不需要/api前缀）
  const apiBaseUrl = process.env.VUE_APP_API_BASE_URL || 'http://localhost:5000/api';
  const baseUrl = apiBaseUrl.replace('/api', '');
  
  // 对于uploads目录的图片，直接调用后端API
  if (imagePath.startsWith('uploads/')) {
    return `${baseUrl}/assets/${imagePath}`;
  }
  
  // 对于以/uploads/开头的路径，转换为后端API路径
  if (imagePath.startsWith('/uploads/')) {
    return `${baseUrl}/assets${imagePath}`;
  }
  
  // 对于以assets/uploads/开头的路径，直接调用后端API
  if (imagePath.startsWith('assets/uploads/')) {
    return `${baseUrl}/${imagePath}`;
  }
  
  // 对于以/assets/uploads/开头的路径，直接调用后端API
  if (imagePath.startsWith('/assets/uploads/')) {
    return `${baseUrl}${imagePath}`;
  }
  
  // 其他情况，假设是图片文件名，直接调用后端API的uploads目录
  return `${baseUrl}/assets/uploads/${imagePath}`;
};


// 格式化解决方案描述，处理换行和基本格式
const formattedDescription = computed(() => {
  // 根据当前语言选择描述字段
  const description = locale.value === 'en' 
    ? (solution.value?.descriptionEn || solution.value?.description || '')
    : (solution.value?.description || '');
  if (!description) return '';
  // 将换行符转换为<br>标签
  let html = description.replace(/\n/g, '<br>');
  return html;
});

// 获取启用的段落，按order_index排序
const activeParagraphs = computed(() => {
  if (!solution.value?.paragraphs) return [];
  return solution.value.paragraphs
    .filter(paragraph => paragraph.is_active)
    .sort((a, b) => (a.order_index || 0) - (b.order_index || 0));
});

// 提取加载解决方案详情为独立方法
const loadSolutionDetail = async () => {
  try {
    loading.value = true;
    error.value = null;
    const solutionId = route.params.id;
    if (!solutionId) {
      throw new Error(t('solutions.noSolutionId'));
    }
    
    // 并行加载解决方案基本信息和段落数据，传递语言参数
    const [solutionResponse, paragraphsResponse] = await Promise.all([
      getSolution(solutionId, locale.value),
      getSolutionParagraphs(solutionId, true) // 只获取活跃段落
    ]);
    
    if (!solutionResponse.data) {
      throw new Error(t('solutions.noSolutionData'));
    }
    
    // 确保features字段存在
    if (!solutionResponse.data.features) {
      solutionResponse.data.features = [
        { id: 1, name: t('solutions.professionalCustom') },
        { id: 2, name: t('solutions.efficientReliable') },
        { id: 3, name: t('solutions.techLeading') },
        { id: 4, name: t('solutions.servicePerfect') }
      ];
    }
    
    // 将段落数据添加到解决方案对象中
    solutionResponse.data.paragraphs = paragraphsResponse.data?.data || [];
    
    solution.value = solutionResponse.data;
  } catch (err) {
    console.error('加载解决方案详情失败:', err);
    error.value = err.message || t('solutions.loadDetailFailed');
    solution.value = null;
  } finally {
    loading.value = false;
  }
};



// 格式化文本内容
const formatTextContent = (content) => {
  if (!content) return '';
  
  // 处理换行符
  let formattedContent = content.replace(/\n/g, '<br>');
  
  // 处理简单的Markdown格式
  formattedContent = formattedContent
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') // 粗体
    .replace(/\*(.*?)\*/g, '<em>$1</em>') // 斜体
    .replace(/`(.*?)`/g, '<code>$1</code>') // 代码
    .replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" target="_blank">$1</a>'); // 链接
  
  return formattedContent;
};

// 解析混合内容
const parseMixedContent = (content) => {
  if (!content) return [];
  
  try {
    // 如果content是字符串，尝试解析为JSON
    if (typeof content === 'string') {
      return JSON.parse(content);
    }
    // 如果content已经是数组，直接返回
    if (Array.isArray(content)) {
      return content;
    }
    // 如果是对象但不是数组，包装成数组
    return [content];
  } catch (error) {
    console.error('解析混合内容失败:', error);
    // 如果解析失败，将其作为文本内容处理
    return [{ type: 'text', content: content }];
  }
};

// 注意：formatParagraphContent已被formatTextContent替代





// 监听语言变化，重新加载数据
watch(() => locale.value, () => {
  loadSolutionDetail();
});

onMounted(() => {
  loadSolutionDetail();
});
</script>

<style scoped lang="scss">
@import '@/styles/nav_section';
/* 原有解决方案页面样式 */
@import '@/styles/sections/homepage/main_menu';
@import '@/styles/sections/product_page/product_head';
@import '@/styles/sections/product_page/product_detail';
@import '@/styles/sections/homepage/footer_section';

/* 解决方案详情页面样式 */
.solution-detail-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 50%, #f0f2f5 100%);
  padding: 20px 0;
}

.solution-hero-section {
  position: relative;
  height: 90vh;
  background-color: #f5f5f5;
  background-image: url('@/assets/solution_banner.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  display: flex;
  align-items: center;
  justify-content: center;
}

.solution-container {
  max-width: 1200px;
  margin: 40px auto 0;
  padding: 0 20px;
}

/* 段落区域样式 */
.solution-sections {
  margin-top: 30px;
}

.dynamic-paragraphs {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.section {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(30, 64, 175, 0.1);
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
}



.section-header {
  padding: 15px 40px 10px;
  background: transparent;
  position: relative;
  overflow: hidden;
}

.section-title {
  font-size: 2.2rem;
  font-weight: 700;
  color: #2c3e50;
  margin: 0;
  text-align: left;
  position: relative;
  z-index: 1;
  letter-spacing: -0.3px;
}

.section-divider {
  height: 4px;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0.8) 0%, rgba(255, 255, 255, 0.3) 50%, transparent 100%);
  border-radius: 2px;
  margin-top: 15px;
  position: relative;
  z-index: 1;
}

.section-content {
  padding: 20px;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
}

.default-sections {
  margin-top: 30px;
}

.default-sections .section {
  text-align: center;
  padding: 60px 40px;
}

.default-sections .section-header {
  display: none;
}

.default-sections .section-content {
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-radius: 20px;
  padding: 60px 40px;
}

/* 段落内容样式 */
.paragraph-text {
  line-height: 1.8;
  color: #333;
  font-size: 16px;
  text-align: left;
  padding: 20px;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-radius: 12px;
  border-left: 4px solid #3b82f6;
  margin: 7px 0;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
}

.paragraph-table {
  margin: 10px 0;
  overflow-x: auto;
  text-align: left;
}

.content-table {
  width: 100%;
  border-collapse: collapse;
  margin: 7px 0;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  overflow: hidden;
  background: white;
  table-layout: fixed;
}

.content-table th {
  background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%);
  color: white;
  padding: 18px 15px;
  text-align: left;
  font-weight: 600;
  font-size: 15px;
  letter-spacing: 0.5px;
  white-space: nowrap;
}

.content-table td {
  padding: 15px;
  border-bottom: 1px solid #e8ecf0;
  font-size: 14px;
  color: #555;
  text-align: left;
  transition: background-color 0.2s ease;
}

.content-table tr:hover {
  background-color: #f8f9fa;
}

.content-table tr:last-child td {
  border-bottom: none;
}

.content-table tr:nth-child(even) {
  background-color: #fafbfc;
}

.paragraph-image {
  text-align: center;
  margin: 12px 0;
  padding: 20px;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-radius: 12px;
  box-shadow: 0 2px 15px rgba(0, 0, 0, 0.05);
}

.content-image {
  max-width: 100%;
  height: auto;
  border-radius: 12px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.12);
  transition: all 0.3s ease;
  border: 3px solid #ffffff;
}

.content-image:hover {
  transform: scale(1.02);
  box-shadow: 0 12px 35px rgba(0, 0, 0, 0.15);
}

.paragraph-mixed {
  .mixed-item {
    margin: 10px 0;
    padding: 15px;
    background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
    border-radius: 10px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  }
  
  .mixed-text {
    line-height: 1.8;
    color: #333;
    font-size: 16px;
    margin: 5px 0;
    text-align: left;
    padding: 15px;
    background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
    border-radius: 8px;
    border-left: 3px solid #3b82f6;
  }
  
  .mixed-image {
    text-align: center;
    margin: 10px 0;
    padding: 15px;
    background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
    border-radius: 10px;
  }
  
  .mixed-table {
    margin: 10px 0;
    overflow-x: auto;
    text-align: left;
  }
  
  .mixed-table .content-table th {
    white-space: nowrap;
  }
}

.table-error, .image-error {
  color: #e74c3c;
  text-align: center;
  padding: 20px;
  background: #fdf2f2;
  border: 1px solid #f5c6cb;
  border-radius: 8px;
  margin: 10px 0;
}

.paragraph-fallback {
  line-height: 1.8;
  color: #333;
  font-size: 16px;
  padding: 15px;
  background: #f8f9fa;
  border-left: 4px solid #007bff;
  border-radius: 0 8px 8px 0;
}



/* 加载和错误状态样式 */
.loading-spinner {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 300px;
  color: white;
  text-align: center;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-top: 4px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-message {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 300px;
  color: white;
  text-align: center;
  background: rgba(220, 53, 69, 0.1);
  border: 1px solid rgba(220, 53, 69, 0.3);
  border-radius: 15px;
  backdrop-filter: blur(10px);
  padding: 40px;
}

.error-icon {
  font-size: 3rem;
  margin-bottom: 15px;
}

.retry-button {
  margin-top: 20px;
  padding: 12px 24px;
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.3s ease;
}

.retry-button:hover {
  background: #c82333;
  transform: translateY(-2px);
}



/* 加载和错误状态样式 */
.loading-spinner {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 20px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
  backdrop-filter: blur(10px);
}

.spinner {
  width: 50px;
  height: 50px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #1e40af;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-spinner p {
  color: #6c757d;
  font-size: 1.1rem;
  margin: 0;
}

.error-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  background: linear-gradient(135deg, #fff5f5 0%, #ffffff 100%);
  border-radius: 20px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
  border: 1px solid #fed7d7;
}

.error-icon {
  font-size: 3rem;
  color: #e53e3e;
  margin-bottom: 20px;
}

.error-message p {
  color: #e53e3e;
  font-size: 1.1rem;
  margin: 0 0 20px 0;
  text-align: center;
}

.retry-button {
  padding: 12px 24px;
  background: linear-gradient(135deg, #e53e3e 0%, #c53030 100%);
  color: white;
  border: none;
  border-radius: 25px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(229, 62, 62, 0.3);
}

.retry-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(229, 62, 62, 0.4);
}

/* 解决方案内容样式 */
.solution-content {
  background: rgba(255, 255, 255, 0.98);
  border-radius: 25px;
  overflow: hidden;
  box-shadow: 0 25px 70px rgba(0, 0, 0, 0.12);
  backdrop-filter: blur(25px);
  border: 1px solid rgba(255, 255, 255, 0.8);
}

/* 解决方案头部样式 */
.solution-header {
  padding: 50px 40px;
  background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 50%, #06b6d4 100%);
  border-bottom: none;
  border-radius: 20px;
  margin-bottom: 30px;
  box-shadow: 0 15px 40px rgba(30, 58, 138, 0.3);
  position: relative;
  overflow: hidden;
}

.solution-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="50" cy="50" r="1" fill="%23ffffff" opacity="0.1"/></pattern></defs><rect width="100" height="100" fill="url(%23grain)"/></svg>') repeat;
  pointer-events: none;
}

.solution-category {
  display: inline-block;
  padding: 8px 20px;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border-radius: 25px;
  font-size: 0.95rem;
  font-weight: 600;
  margin-bottom: 20px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  position: relative;
  z-index: 1;
}

.solution-title {
  font-size: 2.8rem;
  font-weight: 800;
  color: white;
  margin-bottom: 20px;
  line-height: 1.2;
  text-shadow: 0 3px 6px rgba(0, 0, 0, 0.15);
  letter-spacing: -0.5px;
  position: relative;
  z-index: 1;
}

.solution-description {
  font-size: 1.15rem;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.7;
  position: relative;
  z-index: 1;
  background: rgba(255, 255, 255, 0.1);
  padding: 20px;
  border-radius: 15px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  margin-top: 15px;
}

/* 标签页导航样式 */
.solution-tabs {
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}

.tab-navigation {
  display: flex;
  overflow-x: auto;
}

.tab-button {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 20px 30px;
  background: none;
  border: none;
  font-size: 1rem;
  font-weight: 500;
  color: #6c757d;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
  position: relative;
  border-bottom: 3px solid transparent;
}

.tab-button:hover {
  color: #495057;
  background: rgba(30, 64, 175, 0.05);
}

.tab-button.active {
  color: #1e40af;
  background: white;
  border-bottom-color: #1e40af;
}

.tab-icon {
  font-size: 1.2rem;
}

.tab-label {
  font-weight: 600;
}

/* 标签页内容样式 */
.tab-content-container {
  min-height: 500px;
}

.tab-content {
  padding: 40px;
}

.content-header {
  margin-bottom: 30px;
  text-align: center;
}

.content-header h2 {
  font-size: 2rem;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 10px;
}

.content-subtitle {
  font-size: 1.1rem;
  color: #6c757d;
}

/* 核心优势样式 */
.advantages-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 25px;
}

.advantage-card {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 15px;
  padding: 25px;
  border: 1px solid #dee2e6;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.advantage-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
}

.advantage-number {
  position: absolute;
  top: 20px;
  right: 20px;
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #1e40af, #3b82f6);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 1.1rem;
}

.advantage-content {
  color: #495057;
  line-height: 1.6;
  font-size: 1rem;
  padding-right: 60px;
}

/* 应用场景样式 */
.scenarios-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.scenario-item {
  display: flex;
  gap: 20px;
  padding: 25px;
  background: white;
  border-radius: 12px;
  border: 1px solid #e9ecef;
  transition: all 0.3s ease;
}

.scenario-item:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.08);
}

.scenario-icon {
  font-size: 2rem;
  flex-shrink: 0;
}

.scenario-content h4 {
  font-size: 1.2rem;
  font-weight: 600;
  color: #2c3e50;
  margin: 0 0 10px 0;
}

.scenario-content p {
  color: #6c757d;
  line-height: 1.6;
  margin: 0;
}

/* 通用无数据样式 */
.no-data {
  text-align: center;
  padding: 60px 20px;
  color: #6c757d;
}

.no-data-icon {
  font-size: 3rem;
  margin-bottom: 15px;
  display: block;
}

.no-data p {
  font-size: 1.1rem;
  margin: 0;
}

/* 新增标签页样式 */
.technical-specs-section,
.implementation-section,
.service-support-section,
.success-cases-section {
  padding: 20px;
}

.spec-group,
.impl-group,
.service-item,
.case-item {
  margin-bottom: 30px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 4px solid #1890ff;
}

.spec-group h3,
.impl-group h3,
.service-item h3,
.case-item h3 {
  color: #1890ff;
  margin-bottom: 15px;
  font-size: 18px;
  font-weight: 600;
}

.spec-group p,
.impl-group p,
.service-item p {
  color: #333;
  line-height: 1.6;
  margin-bottom: 10px;
}

.spec-group ul,
.impl-group ul,
.case-benefits ul {
  list-style: none;
  padding: 0;
}

.spec-group li,
.impl-group li,
.case-benefits li {
  padding: 8px 0;
  border-bottom: 1px solid #e8e8e8;
  position: relative;
  padding-left: 20px;
}

.spec-group li:before,
.impl-group li:before,
.case-benefits li:before {
  content: '•';
  color: #1890ff;
  font-weight: bold;
  position: absolute;
  left: 0;
}

.phases-timeline {
  position: relative;
}

.phase-item {
  margin-bottom: 20px;
  padding: 15px;
  background: white;
  border-radius: 6px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.phase-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.phase-header h4 {
  color: #1890ff;
  margin: 0;
  font-size: 16px;
}

.phase-duration {
  background: #e6f7ff;
  color: #1890ff;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
}

.phase-description {
  color: #666;
  margin: 0;
  line-height: 1.5;
}

.case-description {
  color: #666;
  line-height: 1.6;
  margin-bottom: 15px;
}

.case-benefits h4 {
  color: #52c41a;
  font-size: 14px;
  margin-bottom: 10px;
}

.case-benefits {
  background: #f6ffed;
  padding: 15px;
  border-radius: 6px;
  border-left: 3px solid #52c41a;
}
/* 技术规格样式 */
.technical-specs-container {
  display: flex;
  flex-direction: column;
  gap: 30px;
}

.spec-category {
  background: white;
  border-radius: 12px;
  border: 1px solid #e9ecef;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.spec-header {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 20px 25px;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid #dee2e6;
}

.spec-icon {
  font-size: 1.5rem;
}

.spec-header h3 {
  margin: 0;
  font-size: 1.3rem;
  font-weight: 600;
  color: #2c3e50;
}

/* 实施方案样式 - 参考技术规格设计 */
.implementation-container {
  display: flex;
  flex-direction: column;
  gap: 30px;
}

.implementation-section {
  background: white;
  border-radius: 12px;
  border: 1px solid #e9ecef;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.impl-header {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 20px 25px;
  background: linear-gradient(135deg, #f0f8ff 0%, #e6f3ff 100%);
  border-bottom: 1px solid #b3d9ff;
}

.impl-icon {
  font-size: 1.5rem;
}

.impl-header h3 {
  margin: 0;
  font-size: 1.3rem;
  font-weight: 600;
  color: #1e3a8a;
}

.impl-content {
  padding: 25px;
}

.phase-card {
  display: flex;
  align-items: flex-start;
  gap: 20px;
  padding: 20px;
  background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  margin-bottom: 15px;
  transition: all 0.3s ease;
}

.phase-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.phase-number {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 1.1rem;
  flex-shrink: 0;
}

.phase-details h4 {
  margin: 0 0 8px 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #1e40af;
}

.phase-duration {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: #dbeafe;
  color: #1e40af;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 500;
  margin-bottom: 10px;
}

.phase-description {
  color: #64748b;
  line-height: 1.6;
  margin: 0;
}

.simple-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid #f1f5f9;
}

.simple-item:last-child {
  border-bottom: none;
}

.item-bullet {
  color: #3b82f6;
  font-weight: bold;
  font-size: 1.2rem;
}

.impl-simple {
  text-align: center;
  color: #64748b;
  padding: 20px;
  font-style: italic;
}

/* 服务支持样式 - 参考技术规格设计 */
.support-container {
  display: flex;
  flex-direction: column;
  gap: 30px;
}

.support-category {
  background: white;
  border-radius: 12px;
  border: 1px solid #e9ecef;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.support-header {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 20px 25px;
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
  border-bottom: 1px solid #bbf7d0;
}

.support-icon {
  font-size: 1.5rem;
}

.support-header h3 {
  margin: 0;
  font-size: 1.3rem;
  font-weight: 600;
  color: #166534;
}

.support-content {
  padding: 25px;
}

.support-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid #f0fdf4;
}

.support-item:last-child {
  border-bottom: none;
}

.support-bullet {
  color: #16a34a;
  font-weight: bold;
  font-size: 1.1rem;
  margin-top: 2px;
}

.support-simple {
  background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 20px;
  color: #475569;
  line-height: 1.6;
}

.support-simple p {
  margin: 0;
}

/* 实施方案嵌套对象样式 */
.impl-object {
  padding: 20px;
}

.impl-sub-section {
  margin-bottom: 25px;
  background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 20px;
}

.impl-sub-section:last-child {
  margin-bottom: 0;
}

.sub-title {
  margin: 0 0 15px 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #1e40af;
  border-bottom: 2px solid #dbeafe;
  padding-bottom: 8px;
}

.sub-list {
  margin: 0;
}

.sub-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 8px 0;
  border-bottom: 1px solid #f1f5f9;
}

.sub-item:last-child {
  border-bottom: none;
}

.sub-bullet {
  color: #3b82f6;
  font-weight: bold;
  margin-top: 2px;
}

.sub-text {
  margin: 0;
  color: #475569;
  line-height: 1.6;
}

.sub-text p {
  margin: 0;
}

/* 服务支持嵌套对象样式 */
.support-object {
  padding: 20px;
}

.support-sub-section {
  margin-bottom: 25px;
  background: linear-gradient(135deg, #f0fdf4 0%, #ffffff 100%);
  border: 1px solid #dcfce7;
  border-radius: 8px;
  padding: 20px;
}

.support-sub-section:last-child {
  margin-bottom: 0;
}

.support-sub-title {
  margin: 0 0 15px 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #166534;
  border-bottom: 2px solid #bbf7d0;
  padding-bottom: 8px;
}

.support-sub-list {
  margin: 0;
}

.support-sub-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 8px 0;
  border-bottom: 1px solid #f0fdf4;
}

.support-sub-item:last-child {
  border-bottom: none;
}

.support-sub-bullet {
  color: #16a34a;
  font-weight: bold;
  margin-top: 2px;
}

.support-sub-text {
  margin: 0;
  color: #475569;
  line-height: 1.6;
}

.support-sub-text p {
  margin: 0;
}

.spec-table-container {
  padding: 0;
}

.spec-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.spec-table th {
  background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%);
  color: white;
  padding: 15px 20px;
  text-align: left;
  font-weight: 600;
  font-size: 15px;
  border: none;
}

.spec-table th:first-child {
  width: 35%;
}

.spec-table td {
  padding: 15px 20px;
  border-bottom: 1px solid #f1f3f4;
  vertical-align: top;
}

.spec-table tr:hover {
  background-color: #f8f9fa;
}

.spec-table tr:last-child td {
  border-bottom: none;
}

.spec-label {
  font-weight: 600;
  color: #495057;
}

.spec-value {
  color: #6c757d;
  line-height: 1.5;
}

.spec-simple {
  padding: 25px;
  text-align: center;
  color: #6c757d;
}

/* 原有表格样式 */
.tech-specs-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 20px;
  font-size: 14px;
}

.tech-specs-table th,
.tech-specs-table td {
  padding: 12px 15px;
  border: 1px solid #e8e8e8;
  text-align: left;
}

.tech-specs-table th {
  background-color: #f7f7f7;
  font-weight: bold;
  color: #333;
}

.tech-specs-table tr:nth-child(even) {
  background-color: #fafafa;
}

.tech-specs-table tr:hover {
  background-color: #f5f5f5;
}


/* 应用场景表格样式 */
.application-scenarios-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 20px;
  font-size: 14px;
}

.application-scenarios-table th,
.application-scenarios-table td {
  padding: 12px 15px;
  border: 1px solid #e8e8e8;
  text-align: left;
}

.application-scenarios-table th {
  background-color: #f7f7f7;
  font-weight: bold;
  color: #333;
}

.application-scenarios-table tr:nth-child(even) {
  background-color: #fafafa;
}

.application-scenarios-table tr:hover {
  background-color: #f5f5f5;
}

/* 解决方案文档样式 */
.documents-section {
  margin-top: 20px;
}

.documents-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
  margin-top: 16px;
}

.document-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border: 1px solid #e9ecef;
  border-radius: 12px;
  text-decoration: none;
  color: #333;
  transition: all 0.3s ease;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  position: relative;
  overflow: hidden;
}

.document-item::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 4px;
  height: 100%;
  background: linear-gradient(180deg, #007bff 0%, #0056b3 100%);
  transform: scaleY(0);
  transition: transform 0.3s ease;
}

.document-item:hover {
  background: linear-gradient(135deg, #ffffff 0%, #f0f7ff 100%);
  border-color: #007bff;
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(0, 123, 255, 0.15);
}

.document-item:hover::before {
  transform: scaleY(1);
}

.document-name {
  font-weight: 600;
  font-size: 16px;
  color: #2c3e50;
  margin-bottom: 4px;
  line-height: 1.4;
}

.document-format {
  background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
  color: white;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 4px rgba(0, 123, 255, 0.3);
}

/* 删除原列表样式 */
.application-scenarios-list {
  display: none;
}

/* 删除原网格样式 */
.tech-specs-grid {
  display: none;
}

/* 调整解决方案特有样式 */
.product-detail-page {
  /* 添加overview-section样式 */
  .overview-section {
    margin-top: 20px;
    padding-top: 20px;
    border-top: 1px solid #eee;
  }

  .overview-section h2 {
    font-size: 18px;
    margin-bottom: 15px;
    color: #333;
  }

  .product-description {
    font-size: 14px;
    line-height: 1.6;
    color: #666;
  }

  /* 调整左侧标题容器宽度，确保有足够空间显示概览内容 */
  .product-title-container {
    flex: 1;
    padding-right: 30px;
  }

  /* 调整右侧图片容器宽度 */
  .product-image-container {
    padding-left: 50px;
    flex: 0 0 40%;
  }
}

/* 移动端响应式适配 */
@media (max-width: 768px) {
  .product-detail-page {
    padding: 0 15px;
  }
  
  .product-header-container {
    flex-direction: column;
  }
  
  .product-title-container {
    padding-right: 0;
    margin-bottom: 20px;
  }
  
  .product-image-container {
    padding-left: 0;
    flex: none;
    text-align: center;
  }
  
  .product-main-image {
    max-width: 100%;
    height: auto;
  }
  
  .tech-specs-table,
  .application-scenarios-table {
    font-size: 13px;
    overflow-x: auto;
    display: block;
    white-space: nowrap;
  }
  
  .tech-specs-table th,
  .tech-specs-table td,
  .application-scenarios-table th,
  .application-scenarios-table td {
    padding: 8px 10px;
  }
  
  .documents-list {
    grid-template-columns: 1fr;
    gap: 12px;
  }
  
  .document-item {
    padding: 16px;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
  
  .document-name {
    font-size: 15px;
    margin-bottom: 0;
  }
  
  .document-format {
    align-self: flex-end;
    font-size: 10px;
    padding: 4px 8px;
  }
  
  .product-features-tags {
    flex-wrap: wrap;
    justify-content: center;
    gap: 8px;
  }
  
  .feature-tag {
    font-size: 12px;
    padding: 4px 8px;
  }
  
  .product-tabs {
    overflow-x: auto;
  }
  
  .tab-buttons {
    min-width: max-content;
  }
  
  .tab-buttons button {
    font-size: 14px;
    padding: 8px 12px;
  }
}

@media (max-width: 480px) {
  .product-detail-page {
    padding: 0 10px;
  }
  
  .product-title-container h1 {
    font-size: 20px;
  }
  
  .product-brief {
    font-size: 14px;
  }
  
  .documents-list {
    gap: 10px;
    margin-top: 12px;
  }
  
  .document-item {
    padding: 14px;
    border-radius: 8px;
    gap: 10px;
  }
  
  .document-item:hover {
    transform: translateY(-2px);
  }
  
  .document-name {
    font-size: 14px;
    line-height: 1.3;
  }
  
  .document-format {
    font-size: 9px;
    padding: 3px 6px;
    border-radius: 12px;
  }
  
  .tech-specs-table,
  .application-scenarios-table {
    font-size: 12px;
  }
  
  .loading-spinner,
  .error-message,
  .no-data {
    height: 200px;
    font-size: 16px;
  }
  
  .back-button {
    font-size: 14px;
    padding: 8px 12px;
  }
  
  .document-item {
    padding: 10px 12px;
  }
  
  .document-name {
    font-size: 13px;
  }
  
  .document-format {
    font-size: 11px;
    padding: 3px 6px;
  }
}

/* 段落内容样式 */
.paragraph-content {
  line-height: 1.8;
  color: #333;
  font-size: 16px;
}

.paragraph-content strong {
  font-weight: 600;
  color: #2c3e50;
}

.paragraph-content em {
  font-style: italic;
  color: #7f8c8d;
}

.paragraph-content code {
  background-color: #f8f9fa;
  padding: 2px 6px;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
  font-size: 14px;
  color: #e74c3c;
}

.paragraph-content p {
  margin-bottom: 16px;
}

.paragraph-content ul,
.paragraph-content ol {
  margin: 16px 0;
  padding-left: 24px;
}

.paragraph-content li {
  margin-bottom: 8px;
}

.paragraph-content img {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  margin: 16px 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.paragraph-content table {
  width: 100%;
  border-collapse: collapse;
  margin: 16px 0;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.paragraph-content th,
.paragraph-content td {
  padding: 12px 16px;
  text-align: left;
  border-bottom: 1px solid #e9ecef;
}

.paragraph-content th {
  background-color: #f8f9fa;
  font-weight: 600;
  color: #495057;
}

.paragraph-content tr:hover {
  background-color: #f8f9fa;
}

.default-sections .no-data {
  text-align: center;
  padding: 40px 20px;
  color: #6c757d;
}

.default-sections .no-data-icon {
  font-size: 48px;
  margin-bottom: 16px;
}
</style>